import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, NavLink } from 'react-router-dom';
import {
  ChevronRight, ChevronDown, ChevronLeft, Menu, X, ArrowUp,
  Zap, AlertTriangle, Info, CheckCircle, ExternalLink, Copy, Check,
  Building2, Users, FileText, DollarSign, Shield, MapPin, Phone,
  Scale, ClipboardList, Layers, BookOpen, Clock, Home
} from 'lucide-react';
import SEO from './components/SEO';
import useBreakpoint from './hooks/useBreakpoint';
import useOnline from './hooks/useOnline';
import { useAnalytics } from './hooks/useAnalytics';
import PWAInstallBanner from './components/PWAInstallBanner';
import AdSlot from './components/AdSlot';
import { EmailSubscribeForm, ConsultingIntakeForm } from './components/LeadCaptureForm';
import AdminDashboard from './pages/AdminDashboard';

// ============================================================================
// DESIGN SYSTEM
// ============================================================================
const gradients = {
  primary: 'from-violet-500 to-purple-600',
  secondary: 'from-indigo-500 to-blue-600',
  info: 'from-blue-500 to-cyan-500',
  success: 'from-green-500 to-emerald-500',
  warning: 'from-amber-500 to-orange-500',
  danger: 'from-red-500 to-rose-500',
  emerald: 'from-emerald-500 to-teal-500',
  rose: 'from-rose-500 to-pink-500',
  cyan: 'from-cyan-500 to-blue-500',
  purple: 'from-purple-500 to-violet-600',
  teal: 'from-teal-500 to-cyan-500',
  deepBlue: 'from-blue-600 to-indigo-600',
};

const glassColors = {
  info: 'from-blue-500/20 to-cyan-500/20 border-blue-400/30',
  warning: 'from-amber-500/20 to-orange-500/20 border-amber-400/30',
  success: 'from-green-500/20 to-emerald-500/20 border-green-400/30',
  danger: 'from-red-500/20 to-rose-500/20 border-red-400/30',
  important: 'from-violet-500/20 to-purple-500/20 border-violet-400/30',
};

// Navigation items with routes
const NAV_ITEMS = [
  { path: '/', label: 'Overview', icon: Home, gradient: gradients.purple },
  { path: '/eligibility', label: 'Eligibility', icon: ClipboardList, gradient: gradients.warning },
  { path: '/waiver-programs', label: 'Waiver Programs', icon: FileText, gradient: gradients.teal, 
    children: [
      { path: '/waiver-programs/ibudget', label: 'iBudget Florida' },
      { path: '/waiver-programs/cdc-plus', label: 'CDC+ Program' },
      { path: '/waiver-programs/waitlist', label: 'Waitlist Info' },
    ]
  },
  { path: '/regional-offices', label: 'Regional Offices', icon: MapPin, gradient: gradients.emerald },
  { path: '/services', label: 'Services', icon: Layers, gradient: gradients.cyan },
  { path: '/providers', label: 'Providers', icon: Building2, gradient: gradients.primary },
  { path: '/consumer-rights', label: 'Consumer Rights', icon: Shield, gradient: gradients.danger },
  { path: '/legal-framework', label: 'Legal Framework', icon: Scale, gradient: gradients.deepBlue },
  { path: '/contacts', label: 'Contacts', icon: Phone, gradient: gradients.rose },
];

// ============================================================================
// UI COMPONENTS
// ============================================================================
const GlassCard = ({ children, className = '', gradient = false, hover = true }) => (
  <div className={`
    relative overflow-hidden rounded-2xl
    bg-gradient-to-br from-white/10 to-white/5
    backdrop-blur-xl border border-white/20
    shadow-lg
    ${hover ? 'hover-lift hover:border-white/30 transition-all duration-300' : ''}
    ${gradient ? 'before:absolute before:inset-0 before:bg-gradient-to-br before:from-violet-500/10 before:to-purple-500/5 before:pointer-events-none' : ''}
    ${className}
  `}>
    {children}
  </div>
);

const StatCard = ({ icon: Icon, label, value, gradient = gradients.primary }) => (
  <GlassCard className="p-5">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
      </div>
    </div>
  </GlassCard>
);

const InfoBox = ({ title, children, type = 'info', icon: CustomIcon }) => {
  const icons = { info: Info, warning: AlertTriangle, success: CheckCircle, danger: AlertTriangle, important: Zap };
  const Icon = CustomIcon || icons[type] || Info;
  
  return (
    <div className={`rounded-xl p-4 bg-gradient-to-br ${glassColors[type]} border backdrop-blur-sm my-4`}>
      {title && (
        <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
          <Icon className="w-4 h-4" />{title}
        </h4>
      )}
      <div className="text-gray-300 text-sm leading-relaxed">{children}</div>
    </div>
  );
};

const DataTable = ({ title, headers, rows }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    const text = [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-white/10 my-6">
      {title && (
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-500/20 to-purple-500/20 border-b border-white/10">
          <h4 className="text-sm font-semibold text-violet-300">{title}</h4>
          <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/10">
            {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              {headers.map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-violet-300 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.02]' : ''} hover:bg-violet-500/10 transition-colors`}>
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-3 text-sm text-gray-300">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ListItem = ({ children }) => (
  <li className="flex items-start gap-3 py-1">
    <ChevronRight className="w-4 h-4 mt-1 text-violet-400 flex-shrink-0" />
    <span className="text-gray-300">{children}</span>
  </li>
);

const SectionHeader = ({ icon: Icon, title, subtitle, gradient = gradients.primary }) => (
  <GlassCard gradient className="p-4 sm:p-6 mb-6">
    <div className="flex items-start gap-3 sm:gap-4">
      <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg shadow-violet-500/20 flex-shrink-0`}>
        <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h1 className="text-xl sm:text-2xl font-bold text-white">{title}</h1>
        {subtitle && <p className="text-gray-400 mt-1 text-sm sm:text-base">{subtitle}</p>}
      </div>
    </div>
  </GlassCard>
);

const ContentSection = ({ title, children, gradient = gradients.primary }) => (
  <GlassCard className="p-6 mb-6">
    {title && (
      <div className="flex items-start gap-3 mb-5">
        <div className={`w-1.5 h-8 rounded-full bg-gradient-to-b ${gradient}`} />
        <h2 className="text-xl font-bold text-white">{title}</h2>
      </div>
    )}
    <div className="prose prose-invert max-w-none">
      {children}
    </div>
  </GlassCard>
);

// ============================================================================
// LAYOUT COMPONENT
// ============================================================================
function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();
  const mainRef = useRef(null);
  const { isTablet, isMobile } = useBreakpoint();
  const { isOnline } = useOnline();

  // Auto-collapse sidebar on tablets for more content space
  useEffect(() => {
    if (isTablet) setSidebarCollapsed(true);
  }, [isTablet]);

  // Show labels when sidebar is explicitly open (mobile/tablet) OR not collapsed (desktop)
  const showLabels = sidebarOpen || !sidebarCollapsed;

  useEffect(() => {
    const handleScroll = () => {
      if (mainRef.current) {
        setShowScrollTop(mainRef.current.scrollTop > 400);
      }
    };
    const main = mainRef.current;
    main?.addEventListener('scroll', handleScroll);
    return () => main?.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleExpand = (path) => {
    setExpandedItems(prev => prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]);
  };

  const isActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }} />
      </div>

      <div className="relative z-10 flex h-screen">
        {/* Mobile Overlay */}
        {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

        {/* Sidebar */}
        <aside className={`
          fixed lg:relative z-40 h-screen transition-all duration-500
          ${sidebarOpen ? 'w-72 translate-x-0' : 'w-0 -translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-72'}
          bg-gradient-to-b from-slate-900/95 to-slate-950/95 backdrop-blur-2xl border-r border-white/10
        `}>
          <div className="h-full flex flex-col overflow-hidden">
            {/* Logo */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  {showLabels && (
                    <div>
                      <span className="font-bold text-white">APD NEXUS</span>
                      <p className="text-xs text-gray-500">Resource Platform</p>
                    </div>
                  )}
                </Link>
                <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-2 rounded-xl hover:bg-white/10 transition-colors hidden lg:block">
                  {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </button>
                <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-xl hover:bg-white/10 lg:hidden">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1" aria-label="Main navigation">
              {NAV_ITEMS.map(item => (
                <div key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive: active }) => `
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300
                      ${active || isActive(item.path) ? `bg-gradient-to-r ${item.gradient} shadow-lg` : 'hover:bg-white/10'}
                    `}
                    onClick={(e) => {
                      if (item.children) {
                        e.preventDefault();
                        toggleExpand(item.path);
                      }
                    }}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive(item.path) ? 'bg-white/20' : 'bg-white/5'}`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    {showLabels && (
                      <>
                        <span className="text-sm font-medium truncate flex-1">{item.label}</span>
                        {item.children && (
                          <ChevronDown className={`w-4 h-4 transition-transform ${expandedItems.includes(item.path) ? 'rotate-180' : ''}`} />
                        )}
                      </>
                    )}
                  </NavLink>
                  {/* Subnav */}
                  {item.children && showLabels && expandedItems.includes(item.path) && (
                    <div className="ml-11 mt-1 space-y-0.5 border-l border-white/10 pl-3">
                      {item.children.map(child => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={({ isActive }) => `
                            block px-3 py-1.5 rounded-lg text-sm transition-colors
                            ${isActive ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}
                          `}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {/* Sidebar Ads */}
              {showLabels && (
                <div className="px-3 pt-3 space-y-3">
                  <AdSlot slotId="sidebar-top" />
                  <AdSlot slotId="sidebar-mid" />
                </div>
              )}

              {/* Footer */}
              {showLabels && (
                <div className="p-3 border-t border-white/10">
                  <GlassCard className="p-3 text-center" hover={false}>
                    <p className="text-xs text-gray-500">Last Updated</p>
                    <p className="text-sm text-white font-medium">January 2026</p>
                  </GlassCard>
                </div>
              )}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="sticky top-0 z-20 bg-slate-900/80 backdrop-blur-xl border-b border-white/10">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl hover:bg-white/10 lg:hidden" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </button>
                <nav className="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
                  <Link to="/" className="text-gray-500 hover:text-white transition-colors">APD NEXUS</Link>
                  {location.pathname !== '/' && (
                    <>
                      <ChevronRight className="w-4 h-4 text-gray-600" />
                      <span className="font-medium text-violet-400">
                        {NAV_ITEMS.find(item => isActive(item.path))?.label || 'Page'}
                      </span>
                    </>
                  )}
                </nav>
              </div>
              <a
                href="tel:1-866-273-2273"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl hover:shadow-lg hover:shadow-violet-500/30 transition-all text-sm font-medium"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">1-866-APD-CARES</span>
              </a>
            </div>
          </header>

          {/* Page Content */}
          <main ref={mainRef} className="flex-1 overflow-y-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6" id="main-content">
            <div className="max-w-4xl mx-auto">
              {children}
            </div>
          </main>

          {/* Footer */}
          <footer className="border-t border-white/10 px-4 py-4 bg-slate-900/50">
            <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Built by</span>
                <span className="font-semibold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">codeAmani Labs</span>
              </div>
              <div className="flex items-center gap-4 text-gray-500">
                <span>© 2026</span>
                <a href="https://apd.myflorida.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1">
                  Official APD Site <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </footer>
        </div>

        {/* Offline Banner */}
        {!isOnline && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/90 backdrop-blur-sm text-center py-2 px-4 text-sm font-medium text-slate-900">
            You're offline — some features may be unavailable
          </div>
        )}

        {/* PWA Install Banner */}
        <PWAInstallBanner />

        {/* Scroll to Top */}
        {showScrollTop && (
          <button
            onClick={() => mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 shadow-lg shadow-violet-500/30 hover:scale-110 transition-transform"
            aria-label="Scroll to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// PAGE COMPONENTS
// ============================================================================

// Home Page
function HomePage() {
  return (
    <>
      <SEO page="home" />
      <SectionHeader icon={Building2} title="APD NEXUS" subtitle="Florida Agency for Persons with Disabilities Resource Platform" gradient={gradients.purple} />
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Served" value="36,000+" gradient={gradients.purple} />
        <StatCard icon={Clock} label="Waitlist" value="17,393" gradient={gradients.warning} />
        <StatCard icon={DollarSign} label="Budget" value="$2.6B" gradient={gradients.success} />
        <StatCard icon={MapPin} label="Regions" value="6" gradient={gradients.info} />
      </div>

      <ContentSection title="Mission & Foundation" gradient={gradients.purple}>
        <p className="text-gray-300 leading-relaxed mb-4">
          The Agency for Persons with Disabilities (APD) administers a $2.6 billion annual system serving 36,000+ individuals through its iBudget Medicaid waiver, with another 17,000-21,000 on the waiting list.
        </p>
        <InfoBox type="important" title="Mission Statement">
          Supporting individuals with developmental disabilities and their families in living, learning, and working within their communities by creating multiple pathways to possibilities.
        </InfoBox>
        <p className="text-gray-300 leading-relaxed mt-4">
          APD operates as an independent agency within Florida's health and human services system, housed within DCF for administrative purposes only but functioning as a separate budget entity under Florida Statute §20.197.
        </p>
      </ContentSection>

      <ContentSection title="Leadership & Structure" gradient={gradients.purple}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Director Robert "Bob" Asztalos leads the agency following his August 2025 appointment by Governor DeSantis. A 29-year Navy veteran and former Deputy Executive Director of the Florida Department of Veterans' Affairs, Asztalos oversees a workforce of 2,709 positions across three main divisions and six regional offices spanning all 67 Florida counties.
        </p>
        <DataTable
          title="Organizational Divisions"
          headers={["Division", "Key Responsibilities"]}
          rows={[
            ["Division of Operations", "Regional offices, DD Centers, residential planning, incident reporting, group home licensing, employment services"],
            ["Division of Programs", "iBudget Florida waiver administration, nursing and behavioral services, Quality Assurance, CDC+ program"],
            ["Division of Budget, Planning & IT", "Financial services, budgeting, revenue management, information technology"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Quick Links" gradient={gradients.info}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/eligibility" className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
            <ClipboardList className="w-6 h-6 text-amber-400" />
            <div>
              <p className="font-medium text-white">Check Eligibility</p>
              <p className="text-sm text-gray-400">7 qualifying conditions</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
          </Link>
          <Link to="/waiver-programs/ibudget" className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
            <FileText className="w-6 h-6 text-teal-400" />
            <div>
              <p className="font-medium text-white">iBudget Florida</p>
              <p className="text-sm text-gray-400">Waiver services info</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
          </Link>
          <Link to="/providers" className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
            <Building2 className="w-6 h-6 text-violet-400" />
            <div>
              <p className="font-medium text-white">Become a Provider</p>
              <p className="text-sm text-gray-400">11-step enrollment</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
          </Link>
          <Link to="/contacts" className="flex items-center gap-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
            <Phone className="w-6 h-6 text-rose-400" />
            <div>
              <p className="font-medium text-white">Contact APD</p>
              <p className="text-sm text-gray-400">CARES Line & regional offices</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500 ml-auto" />
          </Link>
        </div>
      </ContentSection>

      {/* Email Subscribe */}
      <div className="mt-8">
        <EmailSubscribeForm source="homepage" />
      </div>
    </>
  );
}

// Eligibility Page
function EligibilityPage() {
  return (
    <>
      <SEO page="eligibility" />
      <SectionHeader icon={ClipboardList} title="Eligibility & Enrollment" subtitle="Requirements and application process for APD services" gradient={gradients.warning} />

      <ContentSection title="Qualifying Developmental Disabilities" gradient={gradients.warning}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Chapter 393 Florida Statutes (Section 393.063) defines seven qualifying developmental disabilities:
        </p>
        <ul className="space-y-2 mb-4">
          <ListItem>Intellectual Disability — IQ ≤70</ListItem>
          <ListItem>Autism</ListItem>
          <ListItem>Cerebral Palsy</ListItem>
          <ListItem>Spina Bifida</ListItem>
          <ListItem>Down Syndrome</ListItem>
          <ListItem>Prader-Willi Syndrome</ListItem>
          <ListItem>Phelan-McDermid Syndrome</ListItem>
          <ListItem>Children ages 3-5 at high risk</ListItem>
        </ul>
        <InfoBox type="important">
          These conditions must manifest before age 18 and constitute a substantial handicap expected to continue indefinitely.
        </InfoBox>
      </ContentSection>

      <ContentSection title="Application Process" gradient={gradients.warning}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Applications may be submitted by mail, in person, or online at <a href="https://applynow.apd.myflorida.com" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">applynow.apd.myflorida.com</a>. Forms available in English, Spanish, and Haitian Creole.
        </p>
        <DataTable
          title="Required Documentation"
          headers={["Category", "Acceptable Documents"]}
          rows={[
            ["Identity", "Birth certificate, Social Security card"],
            ["Citizenship", "US passport, naturalization certificate, immigration documentation"],
            ["Florida Residency", "Driver's license, voter registration, court-filed Declaration of Domicile"],
            ["Disability Diagnosis", "Medical records, IEP/504 Plan, psychological evaluations with Full-Scale IQ"]
          ]}
        />
        <InfoBox type="success">
          If documentation is unavailable, APD funds appropriate evaluations at no cost to the applicant.
        </InfoBox>
      </ContentSection>

      <ContentSection title="Processing Timelines" gradient={gradients.warning}>
        <DataTable
          title="Eligibility Determination Timelines"
          headers={["Process Type", "Timeline", "Notes"]}
          rows={[
            ["Standard Determination", "60 days", "From complete application receipt"],
            ["Crisis Determination", "15 calendar days", "For Category 1 priority"],
            ["QSI Assessment", "Within 60 days", "Face-to-face interview required"],
            ["Support Plan Development", "Within 60 days", "After enrollment confirmed"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Pathway to Services" gradient={gradients.warning}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Following eligibility determination, individuals progress through these steps:
        </p>
        <ol className="space-y-2">
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">1</span>
            <span className="text-gray-300">QSI assessment completed through face-to-face interview</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">2</span>
            <span className="text-gray-300">Individual placed on pre-enrollment list based on priority category</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">3</span>
            <span className="text-gray-300">Annual status reviews verify information and identify changed circumstances</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">4</span>
            <span className="text-gray-300">When funding becomes available, enrollment offered in priority order</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">5</span>
            <span className="text-gray-300">Medicaid financial eligibility confirmed</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">6</span>
            <span className="text-gray-300">Support Coordinator assigned from Qualified Organization</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">7</span>
            <span className="text-gray-300">Person-Centered Support Plan developed within 60 days</span>
          </li>
          <li className="flex items-start gap-3 py-1">
            <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">8</span>
            <span className="text-gray-300">Cost plan approved and services begin</span>
          </li>
        </ol>
        <InfoBox type="info" className="mt-4">
          Individuals on the waitlist may access Individual and Family Supports (IFS) state-funded services including limited respite, support coordination, and behavioral services.
        </InfoBox>
      </ContentSection>
    </>
  );
}

// Waiver Programs Hub
function WaiverProgramsPage() {
  return (
    <>
      <SEO page="waiverPrograms" />
      <SectionHeader icon={FileText} title="Waiver Programs" subtitle="iBudget Florida, CDC+, and waitlist information" gradient={gradients.teal} />

      <ContentSection title="iBudget Florida Waiver" gradient={gradients.teal}>
        <p className="text-gray-300 leading-relaxed mb-4">
          The iBudget Florida Waiver, authorized under Section 1915(c), provides home and community-based services to 36,000+ eligible individuals as a fee-for-service program outside managed care. FY 2024-25 appropriations exceeded $2.2 billion.
        </p>
        <Link to="/waiver-programs/ibudget" className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors">
          Learn more about iBudget <ChevronRight className="w-4 h-4" />
        </Link>
      </ContentSection>

      <ContentSection title="Consumer Directed Care Plus (CDC+)" gradient={gradients.teal}>
        <p className="text-gray-300 leading-relaxed mb-4">
          CDC+ operates as an optional alternative within the iBudget waiver, trading 8% of the approved cost plan budget for enhanced flexibility and self-direction.
        </p>
        <Link to="/waiver-programs/cdc-plus" className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors">
          Learn more about CDC+ <ChevronRight className="w-4 h-4" />
        </Link>
      </ContentSection>

      <ContentSection title="Pre-Enrollment Waitlist" gradient={gradients.teal}>
        <p className="text-gray-300 leading-relaxed mb-4">
          As of December 2025, 17,393 individuals remain on the pre-enrollment waiting list, prioritized by seven statutory categories.
        </p>
        <Link to="/waiver-programs/waitlist" className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors">
          View waitlist information <ChevronRight className="w-4 h-4" />
        </Link>
      </ContentSection>
    </>
  );
}

// iBudget Page
function IBudgetPage() {
  return (
    <>
      <SEO page="ibudget" />
      <SectionHeader icon={FileText} title="iBudget Florida Waiver" subtitle="$2.2+ billion program serving 36,000+ individuals" gradient={gradients.teal} />

      <ContentSection title="Eligibility Requirements" gradient={gradients.teal}>
        <DataTable
          title="iBudget Eligibility Requirements"
          headers={["Requirement", "Details"]}
          rows={[
            ["Residency", "Florida resident"],
            ["Age", "3 years or older"],
            ["Diagnosis", "One of seven qualifying diagnoses before age 18"],
            ["Level of Care", "Must meet ICF/IID level of care criteria"],
            ["Financial", "Medicaid financial eligibility"],
            ["Functional", "Limitations in 3+ major life activities"]
          ]}
        />
      </ContentSection>

      <ContentSection title="QSI Assessment & Algorithm" gradient={gradients.teal}>
        <p className="text-gray-300 leading-relaxed mb-4">
          The Questionnaire for Situational Information (QSI) assessment, administered every three years by certified APD staff, measures:
        </p>
        <ul className="space-y-2 mb-4">
          <ListItem>Functional Status — 14 daily living questions</ListItem>
          <ListItem>Behavioral Status — Behavioral support needs</ListItem>
          <ListItem>Physical Support Needs — Medical and physical care requirements</ListItem>
        </ul>
        <InfoBox type="info">
          Results feed into the allocation algorithm — a multiple linear regression model that calculates individual budgets. When algorithm allocations prove insufficient, individuals can request Significant Additional Needs (SANs) funding.
        </InfoBox>
      </ContentSection>
    </>
  );
}

// CDC+ Page
function CDCPlusPage() {
  return (
    <>
      <SEO page="cdcPlus" />
      <SectionHeader icon={FileText} title="Consumer Directed Care Plus (CDC+)" subtitle="Self-directed care with enhanced flexibility" gradient={gradients.teal} />

      <ContentSection title="Program Overview" gradient={gradients.teal}>
        <p className="text-gray-300 leading-relaxed mb-4">
          CDC+ operates as an optional alternative within the iBudget waiver, trading 8% of the approved cost plan budget for enhanced flexibility and self-direction.
        </p>
        <DataTable
          title="CDC+ Key Features"
          headers={["Feature", "Details"]}
          rows={[
            ["Budget Trade-off", "8% of cost plan for enhanced flexibility"],
            ["Authorization", "No prior authorization after budget established"],
            ["Family Employment", "Ability to hire family members"],
            ["Rollover", "Unused funds can roll over"],
            ["Admin Fee", "4% (maximum $160/month)"],
            ["Helpline", "1-866-761-7043"]
          ]}
        />
      </ContentSection>
    </>
  );
}

// Waitlist Page
function WaitlistPage() {
  return (
    <>
      <SEO page="waitlist" />
      <SectionHeader icon={Clock} title="Pre-Enrollment Waitlist" subtitle="17,393 individuals waiting as of December 2025" gradient={gradients.warning} />

      <ContentSection title="Priority Categories" gradient={gradients.warning}>
        <DataTable
          title="Waitlist Priority Categories"
          headers={["Category", "Description", "Count", "Percentage"]}
          rows={[
            ["1 (Highest)", "Crisis—homeless, life-threatening", "0", "0%"],
            ["2", "Child welfare transitions", "0", "0%"],
            ["3", "Caregiver condition within 12 months", "122", "0.7%"],
            ["4", "Caregiver age 60+", "348", "2%"],
            ["5", "School graduation within 12 months", "10", "0.1%"],
            ["6", "Adults 21+ not meeting higher criteria", "9,029", "51.9%"],
            ["7", "Under age 21 not meeting higher criteria", "7,884", "45.3%"]
          ]}
        />
        <InfoBox type="warning">
          Categories 6 and 7 comprise 97% of the current waitlist. Crisis enrollment requires determination within 15 calendar days.
        </InfoBox>
      </ContentSection>

      <ContentSection title="HB 1103 — Managed Care (2025)" gradient={gradients.warning}>
        <p className="text-gray-300 leading-relaxed mb-4">
          HB 1103, signed June 2025, transformed the IDD Pilot Program into a statewide managed care option.
        </p>
        <DataTable
          title="HB 1103 Key Provisions"
          headers={["Provision", "Details"]}
          rows={[
            ["Contractor", "Florida Community Care"],
            ["Scope", "All waitlist individuals eligible Oct 1, 2025"],
            ["Enrollment", "Requires affirmative opt-in"],
            ["Family Care Council", "New Statewide FCC eff. July 1, 2025"],
            ["Transparency", "Quarterly reports, waitlist data within 5 days"],
            ["Algorithm Study", "Completed November 2025"]
          ]}
        />
      </ContentSection>
    </>
  );
}

// Regional Offices Page
function RegionalOfficesPage() {
  return (
    <>
      <SEO page="regionalOffices" />
      <SectionHeader icon={MapPin} title="Regional Offices" subtitle="6 regions serving all 67 Florida counties" gradient={gradients.emerald} />

      <ContentSection title="Regional Coverage Map" gradient={gradients.emerald}>
        <DataTable
          title="Regional Office Directory"
          headers={["Region", "Counties", "Key Areas", "Population Served"]}
          rows={[
            ["Northwest", "16 counties", "Escambia, Leon, Bay, Okaloosa", "~4,200"],
            ["Northeast", "20 counties", "Duval, Alachua, Volusia, St. Johns", "~5,800"],
            ["Central", "12 counties", "Orange, Brevard, Polk, Seminole", "~7,200"],
            ["Suncoast", "11 counties", "Hillsborough, Pinellas, Lee, Collier", "~6,100"],
            ["Southeast", "6 counties", "Palm Beach, Broward, Martin", "~8,400"],
            ["Southern", "2 counties", "Miami-Dade, Monroe", "~4,300"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Residential Facilities" gradient={gradients.emerald}>
        <p className="text-gray-300 leading-relaxed mb-4">
          APD operates three residential facilities providing specialized care:
        </p>
        <DataTable
          title="APD Residential Facilities"
          headers={["Facility", "Location", "Description", "Capacity"]}
          rows={[
            ["Tacachale", "Gainesville", "24-hour structured care", "200+ individuals"],
            ["Sunland Center", "Marianna", "24-hour structured care", "200+ individuals"],
            ["DD Defendant Program", "Chattahoochee", "Secure beds for criminal justice system", "180 beds"]
          ]}
        />
      </ContentSection>
    </>
  );
}

// Services Page
function ServicesPage() {
  return (
    <>
      <SEO page="services" />
      <SectionHeader icon={Layers} title="Services Catalog" subtitle="Complete guide to APD service families and rates" gradient={gradients.cyan} />

      <ContentSection title="Life Skills Development (Family 1)" gradient={gradients.cyan}>
        <DataTable
          title="Life Skills Services"
          headers={["Service", "Description", "Rate Range", "Limits"]}
          rows={[
            ["Companion", "Non-medical care, supervision, socialization", "$2.68-$5.63/15 min", "56 hrs/week combined"],
            ["Supported Employment", "Job coaching, competitive employment", "$8.39-$10.77/15 min", "Individual/group models"],
            ["Adult Day Training", "Self-help, socialization, adaptive skills", "$6.70-$22.49/hr", "Age 22+ or HS graduate"],
            ["Prevocational Services", "Employment preparation", "Varies", "36-month max"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Personal Supports (Family 3)" gradient={gradients.cyan}>
        <DataTable
          title="Personal Support Services"
          headers={["Service", "Description", "Rate Range"]}
          rows={[
            ["Personal Supports", "Self-care, daily living, community integration", "$3.39-$6.78/15 min"],
            ["Respite Care", "Temporary caregiver relief (under 21)", "$3.39-$7.02/15 min"],
            ["Skilled Nursing Respite", "Respite with nursing care", "$7.02/15 min"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Residential Services (Family 4)" gradient={gradients.cyan}>
        <DataTable
          title="Residential Habilitation Rates"
          headers={["Level", "Daily Rate", "Monthly Range", "Description"]}
          rows={[
            ["Basic", "$57-$69", "$1,687-$2,024", "Minimal support needs"],
            ["Minimal", "$115-$138", "$3,371-$4,045", "Low support needs"],
            ["Moderate", "$173-$208", "$5,058-$6,070", "Moderate support needs"],
            ["Extensive 1", "$233-$279", "$6,804-$8,165", "High support needs"],
            ["Extensive 2", "$306-$367", "$8,939-$10,727", "Very high support needs"],
            ["Intensive Behavior", "Up to $545/day", "Up to $15,893/mo", "Complex behavioral needs"],
            ["Enhanced Intensive", "Up to $1,412/day", "Up to $41,168/mo", "Most complex needs"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Support Coordination (Family 5)" gradient={gradients.cyan}>
        <DataTable
          title="Support Coordination Levels"
          headers={["Level", "Monthly Rate", "Description"]}
          rows={[
            ["Limited", "$90.79", "Individuals with fewer support needs"],
            ["Full", "$181.55", "Standard coordination services"],
            ["Enhanced", "$439.35", "Complex needs requiring intensive coordination"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Therapeutic Services (Family 6)" gradient={gradients.cyan}>
        <DataTable
          title="Behavior Analysis Services"
          headers={["Level", "Credentials Required", "Rate Range"]}
          rows={[
            ["Level 1", "BCBA + 3 years experience", "$15.76-$23.31/15 min"],
            ["Level 2", "BCBA/CBA", "$13.76-$21.15/15 min"],
            ["Level 3", "BCaBA with supervision", "$8.57-$16.92/15 min"]
          ]}
        />
        <ul className="space-y-2 mt-4">
          <ListItem>Occupational/Physical/Speech Therapy — $17.78/15 min treatment, $53.84 evaluation</ListItem>
          <ListItem>Private Duty Nursing (LPN) — $7.02/15 min</ListItem>
          <ListItem>Private Duty Nursing (RN) — $8.08/15 min</ListItem>
          <ListItem>Mental Health Counseling — $11.95-$16.38/15 min, $142.31 assessment</ListItem>
        </ul>
      </ContentSection>
    </>
  );
}

// Providers Page
function ProvidersPage() {
  return (
    <>
      <SEO page="providers" />
      <SectionHeader icon={Building2} title="Provider Information" subtitle="Enrollment process, rates, and quality assurance" gradient={gradients.primary} />

      <ContentSection title="11-Step Enrollment Process" gradient={gradients.primary}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Provider enrollment requires completing all of the following steps:
        </p>
        <ol className="space-y-2">
          {[
            "Submit Provider Applicant Access Request for Background Screening Clearinghouse",
            "Register in Clearinghouse portal (5-10 business days processing)",
            "Complete fingerprinting through authorized Livescan Vendor",
            "Complete APD iBudget provider enrollment application",
            "Submit application with screening results to regional provider enrollment specialist",
            "Receive draft iBudget Provider Service Listing Letter",
            "Complete Medicaid Provider Application through AHCA Enrollment Wizard",
            "Receive Medicaid Welcome Letter with Provider ID",
            "Provide Medicaid Welcome Letter copy to Regional Provider Enrollment Specialist",
            "Sign Medicaid Waiver Services Agreement (MWSA) with APD",
            "Begin rendering services after receiving Service Authorizations"
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-3 py-1">
              <span className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400 text-sm font-bold flex-shrink-0">{i + 1}</span>
              <span className="text-gray-300">{step}</span>
            </li>
          ))}
        </ol>
        <InfoBox type="warning" className="mt-4">
          Level 2 background screening (FBI/FDLE) through the Care Provider Background Screening Clearinghouse is mandatory. Screening must be complete before any direct contact with vulnerable persons.
        </InfoBox>
      </ContentSection>

      <ContentSection title="Rate Structure" gradient={gradients.primary}>
        <DataTable
          title="Provider Rate Categories"
          headers={["Category", "Description", "Rate Impact"]}
          rows={[
            ["Solo Providers", "Individuals personally rendering services", "Standard rates"],
            ["Agency Providers", "Organizations with 2+ employees including owner", "Higher rates for overhead"],
            ["Geographic Adjustment", "Palm Beach, Broward, Miami-Dade, Monroe", "Higher rates"]
          ]}
        />
        <InfoBox type="success">
          Rate increases took effect July 1, 2024, funded by $200 million in legislative appropriations.
        </InfoBox>
      </ContentSection>

      <ContentSection title="Incident Reporting Requirements" gradient={gradients.primary}>
        <DataTable
          title="Incident Reporting Timelines"
          headers={["Incident Type", "Examples", "Required Timeline"]}
          rows={[
            ["Critical Incidents", "Death, sexual misconduct, life-threatening injury, abuse allegations", "Within 1 HOUR"],
            ["Reportable Incidents", "Injuries, hospitalizations, behavioral incidents", "Within 1 BUSINESS DAY"],
            ["Follow-up Reports", "Updates on reported incidents", "Within 5 BUSINESS DAYS"]
          ]}
        />
        <InfoBox type="danger">
          Florida Abuse Hotline: 1-800-962-2873 (24/7). Failure to report constitutes a criminal offense.
        </InfoBox>
      </ContentSection>
    </>
  );
}

// Consumer Rights Page
function ConsumerRightsPage() {
  return (
    <>
      <SEO page="consumerRights" />
      <SectionHeader icon={Shield} title="Consumer Rights" subtitle="Bill of Rights, fair hearings, and advocacy resources" gradient={gradients.danger} />

      <ContentSection title="Bill of Rights (§393.13)" gradient={gradients.danger}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Florida Statute §393.13 enumerates specific rights for all persons with developmental disabilities:
        </p>
        <ul className="space-y-2">
          <ListItem>Dignity, privacy, and humane care free from abuse, neglect, and exploitation</ListItem>
          <ListItem>Religious freedom without restriction</ListItem>
          <ListItem>Services in the least restrictive conditions necessary</ListItem>
          <ListItem>Education and training regardless of age or disability degree</ListItem>
          <ListItem>Social interaction and community participation</ListItem>
          <ListItem>Physical exercise and recreation</ListItem>
          <ListItem>Freedom from unnecessary restraint, isolation, and excessive medication</ListItem>
          <ListItem>Consent to or refuse treatment (subject to guardian powers)</ListItem>
          <ListItem>Non-discrimination in publicly funded programs</ListItem>
          <ListItem>Right to vote</ListItem>
        </ul>
      </ContentSection>

      <ContentSection title="Fair Hearing Rights" gradient={gradients.danger}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Fair hearings may be requested when APD denies applications, denies/reduces/terminates services, or fails to act with reasonable promptness.
        </p>
        <DataTable
          title="Fair Hearing Filing Requirements"
          headers={["Type", "Filing Deadline", "Submit To"]}
          rows={[
            ["Medicaid-related hearings", "90 days of Notice", "APD Agency Clerk"],
            ["Non-Medicaid issues", "21 calendar days", "APD Agency Clerk"],
            ["Continue services during appeal", "10 calendar days", "APD Agency Clerk"]
          ]}
        />
        <InfoBox type="info">
          APD Agency Clerk: 4030 Esplanade Way, Suite 380, Tallahassee, FL 32399-0950
        </InfoBox>
      </ContentSection>

      <ContentSection title="Advocacy Organizations" gradient={gradients.danger}>
        <DataTable
          title="Key Advocacy Resources"
          headers={["Organization", "Phone", "Services"]}
          rows={[
            ["Disability Rights Florida", "1-800-342-0823", "Free legal advocacy, P&A system"],
            ["FL Developmental Disabilities Council", "1-800-580-7801", "Policy advocacy, Partners in Policymaking"],
            ["The Arc of Florida", "(850) 921-0460", "Human rights, local chapters statewide"],
            ["Family Care Council Florida", "1-800-470-8101", "15 local councils advising APD"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Guardianship Alternatives" gradient={gradients.danger}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Florida law provides alternatives that preserve autonomy while providing support:
        </p>
        <DataTable
          title="Guardianship Options"
          headers={["Option", "Statute", "Description"]}
          rows={[
            ["Guardian Advocacy", "§393.12", "Simplified process WITHOUT formal incapacity determination"],
            ["Supported Decision-Making", "HB 73 (eff. July 2024)", "Retain authority with assistance from trusted supporters"]
          ]}
        />
      </ContentSection>
    </>
  );
}

// Legal Framework Page
function LegalFrameworkPage() {
  return (
    <>
      <SEO page="legalFramework" />
      <SectionHeader icon={Scale} title="Legal Framework" subtitle="Chapter 393 and Administrative Code 65G" gradient={gradients.deepBlue} />

      <ContentSection title="Chapter 393 Florida Statutes" gradient={gradients.deepBlue}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Chapter 393 serves as the enabling legislation for all developmental disabilities services in Florida.
        </p>
        <DataTable
          title="Key Chapter 393 Sections"
          headers={["Section", "Title", "Purpose"]}
          rows={[
            ["§393.063", "Definitions", "Defines 7 qualifying developmental disabilities"],
            ["§393.065", "Application & Eligibility", "Seven-tier pre-enrollment priority system"],
            ["§393.0651", "Support Plans", "Family/Individual Support Plan requirements (60-day completion)"],
            ["§393.0662", "iBudget System", "Individual budget allocations through algorithm"],
            ["§393.0663", "Support Coordination", "Requirements for Qualified Organizations"],
            ["§393.13", "Bill of Rights", "Rights for Persons with Developmental Disabilities"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Administrative Code Chapter 65G" gradient={gradients.deepBlue}>
        <p className="text-gray-300 leading-relaxed mb-4">
          Chapter 65G implements the statutes through detailed operational rules:
        </p>
        <DataTable
          title="Administrative Code Chapters"
          headers={["Chapter", "Subject Area"]}
          rows={[
            ["65G-2", "Residential Facilities"],
            ["65G-3", "Service Termination Procedures"],
            ["65G-4", "Services and Support"],
            ["65G-7", "Medication Administration"],
            ["65G-8", "Reactive Strategies"],
            ["65G-10", "Training Requirements"],
            ["65G-11", "Waitlist Procedures"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Federal Compliance" gradient={gradients.deepBlue}>
        <p className="text-gray-300 leading-relaxed mb-4">
          APD must maintain compliance with federal Medicaid requirements under Section 1915(c) of the Social Security Act, which authorizes Home and Community-Based Services waivers.
        </p>
        <InfoBox type="info" title="CMS HCBS Settings Final Rule (2014)">
          Mandates that all waiver services be delivered in settings that are integrated, support community access, and respect individual rights. Florida achieved compliance by the March 2023 deadline.
        </InfoBox>
        <InfoBox type="important" title="Olmstead v. L.C. (1999)">
          This Supreme Court decision requires states to provide community-based treatment when professionals determine it appropriate, the individual consents, and it can be reasonably accommodated.
        </InfoBox>
      </ContentSection>
    </>
  );
}

// Contacts Page
function ContactsPage() {
  return (
    <>
      <SEO page="contacts" />
      <SectionHeader icon={Phone} title="Essential Contacts" subtitle="Emergency lines, regional offices, and support resources" gradient={gradients.rose} />

      <ContentSection title="Emergency & Crisis Contacts" gradient={gradients.rose}>
        <DataTable
          title="Emergency Numbers"
          headers={["Service", "Number", "Availability"]}
          rows={[
            ["APD CARES Line", "1-866-273-2273", "Business hours"],
            ["Florida Abuse Hotline", "1-800-962-2873", "24/7"],
            ["CDC+ Helpline", "1-866-761-7043", "Business hours"]
          ]}
        />
      </ContentSection>

      <ContentSection title="APD State Office" gradient={gradients.rose}>
        <div className="text-gray-300 space-y-2">
          <p><strong className="text-white">Address:</strong> 4030 Esplanade Way, Suite 380, Tallahassee, FL 32399-0950</p>
          <p><strong className="text-white">Phone:</strong> (850) 488-4257</p>
          <p><strong className="text-white">Toll-Free:</strong> 1-866-APD-CARES (1-866-273-2273)</p>
          <p><strong className="text-white">Email:</strong> APD.info@apdcares.org</p>
          <p><strong className="text-white">Website:</strong> <a href="https://apd.myflorida.com" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300">apd.myflorida.com</a></p>
        </div>
      </ContentSection>

      <ContentSection title="Support Resources Directory" gradient={gradients.rose}>
        <DataTable
          title="Contact Directory"
          headers={["Organization", "Phone", "Purpose"]}
          rows={[
            ["Disability Rights Florida", "1-800-342-0823", "Legal advocacy"],
            ["Family Care Council FL", "1-800-470-8101", "Family support"],
            ["Qlarant QA", "1-866-254-2075", "Quality assurance"],
            ["iConnect Help Desk", "833-400-3420", "Technical support"],
            ["Early Steps", "1-800-218-0001", "Children under 3"],
            ["Vocational Rehabilitation", "1-800-451-4327", "Employment services"]
          ]}
        />
      </ContentSection>

      <ContentSection title="Technology Systems" gradient={gradients.rose}>
        <DataTable
          title="APD Technology Systems"
          headers={["System", "URL/Access", "Purpose"]}
          rows={[
            ["ABC System", "abc.apd.myflorida.com", "Provider billing and claims management"],
            ["TRAIN Florida", "train.org/florida", "Required provider training LMS"],
            ["Background Clearinghouse", "crw.flclearinghouse.com", "Statewide screening database"],
            ["APD Resource Directory", "resourcedirectory.apd.myflorida.com", "Searchable community resources (38+ topics)"]
          ]}
        />
      </ContentSection>

      {/* Consulting Intake Form */}
      <div className="mt-8">
        <ConsultingIntakeForm sourcePage="contacts" />
      </div>

      {/* Email Subscribe */}
      <div className="mt-6">
        <EmailSubscribeForm source="contacts" />
      </div>
    </>
  );
}

// 404 Page
function NotFoundPage() {
  return (
    <>
      <SEO page="home" />
      <div className="text-center py-16">
        <h1 className="text-6xl font-bold text-violet-400 mb-4">404</h1>
        <h2 className="text-2xl font-bold text-white mb-4">Page Not Found</h2>
        <p className="text-gray-400 mb-8">The page you're looking for doesn't exist or has been moved.</p>
        <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl hover:shadow-lg transition-all">
          <Home className="w-5 h-5" />
          Back to Home
        </Link>
      </div>
    </>
  );
}

// ============================================================================
// APP COMPONENT WITH ROUTING
// ============================================================================
export default function App() {
  useAnalytics();

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/eligibility" element={<EligibilityPage />} />
        <Route path="/waiver-programs" element={<WaiverProgramsPage />} />
        <Route path="/waiver-programs/ibudget" element={<IBudgetPage />} />
        <Route path="/waiver-programs/cdc-plus" element={<CDCPlusPage />} />
        <Route path="/waiver-programs/waitlist" element={<WaitlistPage />} />
        <Route path="/regional-offices" element={<RegionalOfficesPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/providers" element={<ProvidersPage />} />
        <Route path="/consumer-rights" element={<ConsumerRightsPage />} />
        <Route path="/legal-framework" element={<LegalFrameworkPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
}
